import APIProblemTracking from "@/services/api/problem_tracking";
import { plainToClassFromExist } from "class-transformer";
import { ResponsesModel } from "@/models/classes/responses";
import { ResponseModel } from "@/models/classes/response";
import { SuccessModel } from "@/models/classes/success";
import {
  ChannelModel,
  StatusModel,
  ProblemGroupModel,
  ProblemTypeModel,
  ProblemModel,
  CustomerWithProblemModel,
  ProblemTrackingCustomerModel,
  ProblemByCustomerModel,
} from "@/models/classes/problem_tracking/problem_tracking";

const API = new APIProblemTracking();

class ProblemTrackingService {
  private getErrorMessage(error: any): string {
    return (
      error.response?.data?.displayMessage ||
      error.response?.data?.message ||
      error.message ||
      "เกิดข้อผิดพลาดในการเชื่อมต่อ"
    );
  }

  async getChannels() {
    try {
      const resData = await API.getChannels();
      const resModel = plainToClassFromExist(
        new ResponsesModel<ChannelModel>(ChannelModel),
        resData,
      );
      if (resModel.status == 200 && typeof resModel.data == "object") {
        return {
          data: resModel.data,
          total: resModel.total,
          result: resModel.result,
        };
      }
      throw new Error(resModel.message || "Failed to fetch channels");
    } catch (err) {
      throw new Error(this.getErrorMessage(err));
    }
  }

  async getStatuses() {
    try {
      const resData = await API.getStatuses();
      const resModel = plainToClassFromExist(
        new ResponsesModel<StatusModel>(StatusModel),
        resData,
      );
      if (resModel.status == 200 && typeof resModel.data == "object") {
        return {
          data: resModel.data,
          total: resModel.total,
          result: resModel.result,
        };
      }
      throw new Error(resModel.message || "Failed to fetch statuses");
    } catch (err) {
      throw new Error(this.getErrorMessage(err));
    }
  }

  async getStatusesWithProblems(problemId: number) {
    try {
      const resData = await API.getStatusesWithProblems(problemId);
      const resModel = plainToClassFromExist(
        new ResponsesModel<StatusModel>(StatusModel),
        resData,
      );
      if (resModel.status == 200 && typeof resModel.data == "object") {
        return {
          data: resModel.data,
          total: resModel.total,
          result: resModel.result,
        };
      }
      throw new Error(resModel.message || "Failed to fetch statuses");
    } catch (err) {
      throw new Error(this.getErrorMessage(err));
    }
  }

  async getCustomers(params: { customerSearchText: string }) {
    try {
      const resData = await API.getCustomers(params);
      const resModel = plainToClassFromExist(
        new ResponsesModel<ProblemTrackingCustomerModel>(
          ProblemTrackingCustomerModel,
        ),
        resData,
      );
      if (resModel.status == 200 && typeof resModel.data == "object") {
        return {
          data: resModel.data,
          total: resModel.total,
          result: resModel.result,
          xTotalCount: resModel.total,
        };
      }
      throw new Error(resModel.message || "Failed to fetch customers");
    } catch (err) {
      throw new Error(this.getErrorMessage(err));
    }
  }

  async getDropdownCustomersForReport(params: { customerSearchText: string }) {
    try {
      const resData = await API.getDropdownCustomersForReport(params);
      const resModel = plainToClassFromExist(
        new ResponsesModel<ProblemTrackingCustomerModel>(
          ProblemTrackingCustomerModel,
        ),
        resData,
      );
      if (resModel.status == 200 && typeof resModel.data == "object") {
        return {
          data: resModel.data,
          total: resModel.total,
          result: resModel.result,
          xTotalCount: resModel.total,
        };
      }
      throw new Error(resModel.message || "Failed to fetch customers");
    } catch (err) {
      throw new Error(this.getErrorMessage(err));
    }
  }

  async getCustomersWithProblems(params: {
    subShopTypeId: number;
    customerSearchText: string;
    statusId: number;
    limit: number;
    offset: number;
  }) {
    try {
      const resData = await API.getCustomersWithProblems(params);
      const resModel = plainToClassFromExist(
        new ResponsesModel<CustomerWithProblemModel>(CustomerWithProblemModel),
        resData,
      );
      if (resModel.status == 200 && typeof resModel.data == "object") {
        return {
          data: resModel.data,
          total: resModel.total,
          result: resModel.result,
          xTotalCount: resModel.total,
        };
      }
      throw new Error(resModel.message || "Failed to fetch customers");
    } catch (err) {
      throw new Error(this.getErrorMessage(err));
    }
  }

  async getProblemsByCustomer(params: {
    customerId: number;
    reportDate: string;
    groupId: number;
    problemTypeId: number;
    statusId: number;
    limit: number;
    offset: number;
  }) {
    try {
      const resData = await API.getProblemsByCustomer(params);
      const resModel = plainToClassFromExist(
        new ResponseModel<ProblemByCustomerModel>(ProblemByCustomerModel),
        resData,
      );
      if (resModel.status == 200 && typeof resModel.data == "object") {
        return {
          data: resModel.data,
          total: resModel.total,
          result: resModel.result,
          xTotalCount: resModel.total,
        };
      }
      throw new Error(resModel.message || "Failed to fetch problems");
    } catch (err) {
      throw new Error(this.getErrorMessage(err));
    }
  }

  async getProblemGroups() {
    try {
      const resData = await API.getProblemGroups();
      const resModel = plainToClassFromExist(
        new ResponsesModel<ProblemGroupModel>(ProblemGroupModel),
        resData,
      );
      if (resModel.status == 200 && typeof resModel.data == "object") {
        return {
          data: resModel.data,
          total: resModel.total,
          result: resModel.result,
        };
      }
      throw new Error(resModel.message || "Failed to fetch problem groups");
    } catch (err) {
      throw new Error(this.getErrorMessage(err));
    }
  }

  async getProblemTypes() {
    try {
      const resData = await API.getProblemTypes();
      const resModel = plainToClassFromExist(
        new ResponsesModel<ProblemTypeModel>(ProblemTypeModel),
        resData,
      );
      if (resModel.status == 200 && typeof resModel.data == "object") {
        return {
          data: resModel.data,
          total: resModel.total,
          result: resModel.result,
        };
      }
      throw new Error(resModel.message || "Failed to fetch problem types");
    } catch (err) {
      throw new Error(this.getErrorMessage(err));
    }
  }

  async createProblem(payload: any) {
    try {
      const resData = await API.createProblem(payload);
      const resModel = plainToClassFromExist(
        new ResponseModel<SuccessModel>(SuccessModel),
        resData,
      );
      if (resModel.status == 200 && typeof resModel.data == "object") {
        return resModel.data;
      }
      throw new Error(resModel.message || "สร้างรายการปัญหาไม่สำเร็จ");
    } catch (err) {
      throw new Error(this.getErrorMessage(err));
    }
  }

  async resolveProblem(problemId: number, payload: any) {
    try {
      const resData = await API.resolveProblem(problemId, payload);
      const resModel = plainToClassFromExist(
        new ResponseModel<SuccessModel>(SuccessModel),
        resData,
      );
      if (resModel.status == 200 && typeof resModel.data == "object") {
        return resModel.data;
      }
      throw new Error(resModel.message || "บันทึกการแก้ไขปัญหาไม่สำเร็จ");
    } catch (err) {
      throw new Error(this.getErrorMessage(err));
    }
  }

  async getProblemDetail(problemId: number) {
    try {
      const resData = await API.getProblemDetail(problemId);
      const resModel = plainToClassFromExist(
        new ResponseModel<ProblemModel>(ProblemModel),
        resData,
      );
      if (resModel.status == 200 && typeof resModel.data == "object") {
        return { data: resModel.data, result: resModel.result };
      }
      throw new Error(resModel.message || "Failed to fetch problem detail");
    } catch (err) {
      throw new Error(this.getErrorMessage(err));
    }
  }

  async approveProblem(problemId: number, payload: { isApprove: boolean }) {
    try {
      const resData = await API.approveProblem(problemId, payload);
      const resModel = plainToClassFromExist(
        new ResponseModel<SuccessModel>(SuccessModel),
        resData,
      );
      if (resModel.status == 200 && typeof resModel.data == "object") {
        return resModel.data;
      }
      throw new Error(resModel.message || "อนุมัติรายการไม่สำเร็จ");
    } catch (err) {
      throw new Error(this.getErrorMessage(err));
    }
  }
}

export { ProblemTrackingService };
